import React, { useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { getDesignTokens } from '@/theme';

// Simplified context that only provides light mode
interface ThemeContextType {
  mode: 'light';
  toggleTheme: () => void;
  setMode: (mode: 'light') => void;
  isDarkMode: false;
}

const ThemeContext = React.createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = (): ThemeContextType => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Always use light mode
  const theme = useMemo(() => createTheme(getDesignTokens()), []);

  // Provide stub functions for backwards compatibility
  const contextValue = useMemo(
    () => ({
      mode: 'light' as const,
      toggleTheme: () => {},
      setMode: () => {},
      isDarkMode: false as const,
    }),
    []
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
